<template>
  <div class="full-height">
    <spinner v-if="loading" />

    <div v-else>
      <template v-if="isCreate || isEdit">
        <alert class="slds-m-bottom--xx-small"  :message="$t('campaignDesigner.targetDefinition.rulesAlert')" type="message" />

        <!-- Bookmarks -->
        <card additional-classes="slds-m-bottom">
          <accordion>
            <accordion-item :title="$t('queryBuilder.bookmarks')">
              <accordion>
                <card additional-classes="slds-card_boundary" v-if="bookmarks.length">
                  <draggable @start="dragStart" @end="dragEnd" v-model="bookmarks" group="rules">
                    <accordion-item v-for="(bookmark, index) in bookmarks" :key="index" :title="bookmark.segmentName" :accordion-item-id="`${index}`">
                      <template #actions>
                        <menu-button
                          hide-check
                          labels-prefix="queryBuilder"
                          position="right"
                          :options="['import', 'delete']"
                          @select="handleSelect($event, bookmark)"/>
                      </template>

                      <group scope="campaign" :value="bookmark" :config="config" :brands="brands" />

                    </accordion-item>
                  </draggable>
                </card>
                <span v-else>{{$t('queryBuilder.noBookmark')}}</span>
              </accordion>
            </accordion-item>
          </accordion>
        </card>
      </template>

      <card class="full-height" :title="$t('campaignDesigner.targetDefinition.segments')" has-icon>
        <template #icon>
          <span class="slds-icon_container slds-icon-standard-segments">
            <svg class="slds-icon slds-icon-standard-segments" aria-hidden="true">
              <use xlink:href="../../../assets/symbols.svg#segments"></use>
            </svg>
          </span>
        </template>
        <template #actions v-if="isCreate || isEdit">
          <ul class="slds-button-group-list">
            <li>
              <button class="slds-button slds-button_neutral" :disabled="!canAddRule" @click="addRule">
                <svg class="slds-button__icon slds-button__icon_left" aria-hidden="true">
                  <use xlink:href="../../../assets/symbols.svg#add"></use>
                </svg>
                {{$t('campaignDesigner.targetDefinition.addRule')}}
              </button>
            </li>
            <li>
              <button class="slds-button slds-button_neutral" :disabled="!canAddRule" @click="showModal">
                <svg class="slds-button__icon slds-button__icon_left" aria-hidden="true">
                  <use xlink:href="../../../assets/symbols.svg#add"></use>
                </svg>
                {{$t('campaignDesigner.targetDefinition.addRuleGroup')}}
              </button>
            </li>
            <li>
              <button class="slds-button slds-button_neutral" :disabled="!localValue.conditions" @click="openPreviewModal">
                <svg class="slds-button__icon slds-button__icon_left" aria-hidden="true">
                  <use xlink:href="../../../assets/symbols.svg#preview"></use>
                </svg>
                {{$t('campaignDesigner.targetDefinition.preview')}}
              </button>
            </li>
            <li>
              <button class="slds-button slds-button_neutral" :disabled="!localValue.conditions" @click="openBookmarkModal">
                <svg class="slds-button__icon slds-button__icon_left" aria-hidden="true">
                  <use xlink:href="../../../assets/symbols.svg#save"></use>
                </svg>
                {{$t('campaignDesigner.targetDefinition.saveSectionAs')}}
              </button>
            </li>
          </ul>
        </template>

        <draggable v-model="localValue.conditions" group="rules">
          <rule-groups ref="groups" scope="campaign" :brands="brands" v-model="localValue" :editable="isCreate || isEdit" @deleteRule="deleteRule" :config="config" :standard-rules="standardRules" />
        </draggable>
      </card>
    </div>

    <transition name="fade">
      <new-group-modal
        @newFromBookmark="createFromBookmark"
        @newGroup="createGroup"
        @close="closeModal"
        :config="config"
        modal-action="add"
        v-if="showAddGroupModal" />
    </transition>

    <transition name="fade">
      <save-bookmark-modal-from-target-definition
        @close="showSaveBookmarkModal = false"
        :bookmark="localValue"
        scope="campaign"
        v-if="showSaveBookmarkModal" />
    </transition>

    <transition name="fade">
      <modal
        :title="$t('queryBuilder.deleteBookmark.title')"
        :destructive-button-text="$t('action.delete')"
        @close="bookmarkToDeleteId = ''"
        @destructiveButtonClick="doDeleteBookmark"
        v-if="bookmarkToDeleteId">
        {{$t('queryBuilder.deleteBookmark.message')}}
      </modal>
    </transition>

    <transition name="fade">
      <modal
        :title="$t('campaignDesigner.targetDefinition.preview')"
        @close="showPreviewModal = false"
        size="large"
        v-if="showPreviewModal">
          <rule-groups
            scope="campaign"
            :brands="brands"
            :config="config"
            :standard-rules="standardRules"
            :value="localValue" />
      </modal>
    </transition>
  </div>
</template>
<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { validate } from '../../utils/utils'
import Accordion from '../../slds/Accordion'
import AccordionItem from '../../slds/AccordionItem'
import Alert from '../../slds/Alert'
import Card from '../../slds/Card'
import Draggable from 'vuedraggable'
import EventBus from '../../utils/EventBus'
import Group from './target-definition/Group'
import HasMode from '../../utils/HasMode'
import MenuButton from '../../slds/MenuButton'
import Modal from '../../Modal'
import RuleGroups from './target-definition/RuleGroups'
import SaveBookmarkModal from './target-definition/SaveBookmarkModal'
import Spinner from '../../Spinner'
export default {
  watch: {
    async 'bookmarks.length' () {
      await this.getBookmarks()
    }
  },
  mixins: [HasMode],
  name: 'TargetDefinition',
  props: {
    loading: Boolean,
    mode: String
  },
  components: {
    Accordion,
    AccordionItem,
    Alert,
    Card,
    Draggable,
    Group,
    MenuButton,
    Modal,
    NewGroupModal: () => import('./target-definition/NewGroupModal'),
    RuleGroups,
    SaveBookmarkModalFromTargetDefinition: SaveBookmarkModal,
    Spinner
  },
  data () {
    return {
      brands: [],
      bookmarks: [],
      bookmarkToDeleteId: '',
      config: {},
      drag: false,
      localValue: {},
      newGroup: {
        operator: '',
        segmentName: ''
      },
      operators: [
        {
          label: this.$t('operators.AND'),
          value: 'AND'
        },
        {
          label: this.$t('operators.OR'),
          value: 'OR'
        },
        {
          label: this.$t('operators.NOT'),
          value: 'NOT'
        }
      ],
      showPreviewModal: false,
      showSaveBookmarkModal: false,
      showAddGroupModal: false,
      standardRules: {}
    }
  },
  methods: {
    async doDeleteBookmark () {
      try {
        this.deleteBookmark(this.bookmarkToDeleteId)
        await this.getBookmarks()
        this.bookmarkToDeleteId = ''
        this.showToast('success', this.$t('message.delete'), 'text')
      } catch (error) {
        console.error(error)
        this.showToast('error', this.$t('general.error'), 'text', error)
      }
      EventBus.$emit('bookmark-deleted')
    },
    dragStart () {
      EventBus.$emit('start-drag', { source: 'bookmark' })
    },
    dragEnd () {
      EventBus.$emit('end-drag', { source: 'bookmark' })
    },
    ...mapActions({
      deleteBookmark: 'salesQueryBuilder/deleteBookmark',
      fetchBookmarks: 'salesQueryBuilder/getBookmarks',
      getSettings: 'settings/getSalesQueryBuilderConfig'
    }),
    async handleSelect (event, bookmark) {
      if (event.value === 'import') {
        this.localValue = _.clone(bookmark)
      } else if (event.value === 'delete') {
        this.bookmarkToDeleteId = bookmark.id
      }
    },
    createFromBookmark (bookmark) {
      this.localValue = {
        conditions: [],
        ...bookmark
      }

      this.closeModal()
    },
    createGroup (event) {
      const { segmentName, operator } = event

      this.localValue = {
        // id: 'item_0',
        segmentName: segmentName,
        type: 'group',
        operator: operator,
        conditions: []
      }

      this.closeModal()
    },
    addRule () {
      this.localValue = {
        type: 'rule',
        table: '',
        field: '',
        operator: '',
        values: ''
      }
    },
    addRuleGroup () {
      const valid = validate(this, 'newGroup_', true)
      if (valid) {
        this.localValue = {
          segmentName: this.newGroup.segmentName,
          type: 'group',
          operator: this.newGroup.operator,
          conditions: []
        }

        this.closeModal()
      }
    },
    async callLoadFromStore () {
      const campaign = this.$store.getters['campaignDesigner/state'].currentCampaign
      this.brands = campaign.brands
      const targetSource = campaign.config.campaignData.targetSource.selected
      const settings = (await this.getSettings()).config
      this.config = Object.entries(settings).reduce((acc, curr) => {
        if (curr[1].targetSource.includes(targetSource)) {
          acc[curr[0]] = curr[1]
        }
        return acc
      }, {})
      await this.getBookmarks()
      const filters = _.cloneDeep(campaign.config.filters)
      this.localValue = filters.selected
      this.standardRules = filters.standard
      EventBus.$on('bookmark-added', async () => {
        await this.getBookmarks()
      })
      EventBus.$on('bookmark-deleted', async () => {
        await this.getBookmarks()
      })
    },
    closeModal () {
      this.showAddGroupModal = false
    },
    openBookmarkModal () {
      if (this.validate()) {
        this.showSaveBookmarkModal = true
      }
    },
    openPreviewModal () {
      if (this.validate()) {
        this.showPreviewModal = true
      }
    },
    deleteRule () {
      this.localValue = {}
    },
    async getBookmarks () {
      const bookmarks = await this.fetchBookmarks({ scope: 'campaign'})
      this.bookmarks = bookmarks.map(bookmark => {
        return {
          ...bookmark.section,
          id: bookmark.id
        }
      })
    },
    getSectionToSave () {
      return {
        config: {
          selected: this.localValue,
          standard: this.standardRules
        }
      }
    },
    showModal () {
      this.showAddGroupModal = true
    },
    validate () {
      return this.$refs.groups.validate()
    }
  },
  async activated () {
    this.$emit('startLoading')
    await this.callLoadFromStore()
    this.$emit('endLoading')
  },
  computed: {
    canAddRule () {
      return _.isEmpty(this.localValue)
    }
  }
}
</script>
<style lang="scss" scoped>
  .and {
    &.cls-1 {
      fill: #fff;
      stroke: #dddbda;
    }

    &.cls-1,
    &.cls-2 {
      stroke-miterlimit: 10;
      stroke-width: 1.5px;
    }

    &.cls-2 {
      fill: #4589ee;
      stroke: #4589ee;
    }

    &.cls-3 {
      fill: #a9ddf3;
    }

    &.cls-4 {
      font-size: 19px;
      fill: #c1c1c1;
      font-family: 'Salesforce Sans';
    }

  }

  .not {
    &.cls-1 {
      fill: #4589ee;
    }

    &.cls-1,
    &.cls-2 {
      stroke: #dddbda;
      stroke-miterlimit: 10;
      stroke-width: 1.5px;
    }

    &.cls-2 {
      fill: #fff;
    }

    &.cls-3 {
      font-size: 19px;
      fill: #c1c1c1;
      font-family: 'Salesforce Sans';
    }

  }

  .or {
    &.cls-1 {
      fill: #fff;
    }

    &.cls-1,
    &.cls-4 {
      stroke: #dddbda;
    }

    &.cls-1,
    &.cls-2,
    &.cls-4 {
      stroke-miterlimit: 10;
      stroke-width: 1.5px;
    }

    &.cls-2,
    &.cls-3,
    &.cls-4 {
      fill: #4589ee;
    }

    &.cls-2 {
      stroke: #4589ee;
    }
    &.cls-5 {
      font-size: 19px;
      fill: #c1c1c1;
      font-family: 'Salesforce Sans';
    }
  }
</style>
